import React from 'react';
import { useTheme, Box, Typography } from '@mui/material';

import { makeStyles } from '@mui/styles';

export const DetailedCFRangeGraph = ({ min, max, factor, average }: any) => {
  const { config } = useTheme();
  const range = max - min || 1;
  const avgPos = ((average - min) / range) * 100;
  const factorPos = ((factor - min) / range) * 100;

  // Determine color for factor value
  let factorColor = 'gray';
  if (factor > average) {
    factorColor = config.colors.cfAbove;
  } else if (factor < average) {
    factorColor = config.colors.cfBelow;
  } else {
    factorColor = config.colors.cfNeutral;
  }

  return (
    <Box
      position="relative"
      width="80%"
      height={38}
      minWidth={60}
      sx={{
        top: 4,
        margin: '0 auto 0 0',
      }}
    >
      {/* Min/Max labels */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          position: 'absolute',
          left: 15,
          top: 28,
          fontSize: 11,
          transform: 'translateY(-50%)',
        }}
      >
        {min}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          position: 'absolute',
          right: -15,
          top: 28,
          fontSize: 11,
          transform: 'translateY(-50%)',
        }}
      >
        {max}
      </Typography>
      {/* Red band: min to industry avg */}
      <Box
        position="absolute"
        left={16}
        top={14}
        height={6}
        bgcolor={config.colors.cfBelow}
        borderRadius={2}
        width={`calc(${avgPos}% )`}
        zIndex={1}
      />
      {/* Green band: industry avg to max */}
      <Box
        position="absolute"
        left={`calc(${avgPos}% + 16px)`}
        top={14}
        height={6}
        bgcolor={config.colors.cfBarGood}
        borderRadius={2}
        width={`calc(${100 - avgPos}% )`}
        zIndex={1}
      />
      {/* Company factor: blue circular dot */}
      <Box
        position="absolute"
        top={13}
        left={`calc(${factorPos}% + 16px - 4px)`}
        width={8}
        height={8}
        bgcolor={config.colors.cfNeutral}
        borderRadius="50%"
        zIndex={4}
      />
      {/* Industry average: vertical thin line */}
      <Box
        position="absolute"
        top={10}
        left={`calc(${avgPos}% + 16px - 1px)`}
        width={2}
        height={14}
        bgcolor="black"
        borderRadius={1}
        zIndex={3}
      />
      {/* Factor value above the dot */}
      <Typography
        variant="caption"
        sx={{
          position: 'absolute',
          left: `calc(${factorPos}% + 16px)`,
          top: -6,
          fontWeight: 500,
          fontSize: 11,
          color: factorColor,
          transform: 'translateX(-50%)',
          zIndex: 5,
        }}
      >
        {factor}
      </Typography>
    </Box>
  );
};

export const DetailedCFRangeGraphLegend = ({ legendContainerProps }: any) => {
  const classes = useClasses();
  return (
    <Box className={classes.legendContainer} {...legendContainerProps}>
      <div className={classes.legendGrid}>
        <span className={classes.industryAverageLine} />
        <span className={classes.legendText}>ICF</span>
      </div>
      <div className={classes.legendGrid}>
        <span className={classes.companyScoreDot} />
        <span className={classes.legendText}>CCF</span>
      </div>
      <div className={classes.legendGrid}>
        <span className={classes.cfLegendBarRed} />
        <span className={classes.legendText}>CCF {'<'} ICF</span>
      </div>
      <div className={classes.legendGrid}>
        <span className={classes.cfLegendBarGreen} />
        <span className={classes.legendText}>CCF {'>'} ICF</span>
      </div>
    </Box>
  );
};

export const DetailedNonLiveCFRangeGraph = ({
  min,
  max,
  factor,
  average,
}: any) => {
  const { config } = useTheme();
  const range = max - min || 1;
  const avgPos = ((average - min) / range) * 100;
  const factorPos = ((factor - min) / range) * 100;

  // Use grey color for factor value
  const factorColor = config.colors.grey6;

  return (
    <Box
      position="relative"
      width="80%"
      height={38}
      minWidth={60}
      sx={{
        top: 4,
        margin: '0 auto 0 0',
      }}
    >
      {/* Min/Max labels */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          position: 'absolute',
          left: 15,
          top: 28,
          fontSize: 11,
          transform: 'translateY(-50%)',
        }}
      >
        {min}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          position: 'absolute',
          right: -15,
          top: 28,
          fontSize: 11,
          transform: 'translateY(-50%)',
        }}
      >
        {max}
      </Typography>
      {/* Grey band: min to industry avg */}
      <Box
        position="absolute"
        left={16}
        top={14}
        height={6}
        bgcolor={config.colors.grey3}
        borderRadius={2}
        width={`calc(${avgPos}% )`}
        zIndex={1}
      />
      {/* Grey band: industry avg to max */}
      <Box
        position="absolute"
        left={`calc(${avgPos}% + 16px)`}
        top={14}
        height={6}
        bgcolor={config.colors.grey4}
        borderRadius={2}
        width={`calc(${100 - avgPos}% )`}
        zIndex={1}
      />
      {/* Company factor: grey circular dot */}
      <Box
        position="absolute"
        top={13}
        left={`calc(${factorPos}% + 16px - 4px)`}
        width={8}
        height={8}
        bgcolor={config.colors.grey2}
        borderRadius="50%"
        zIndex={4}
      />
      {/* Industry average: vertical thin line */}
      <Box
        position="absolute"
        top={10}
        left={`calc(${avgPos}% + 16px - 1px)`}
        width={2}
        height={14}
        bgcolor={config.colors.grey6}
        borderRadius={1}
        zIndex={3}
      />
      {/* Factor value above the dot */}
      <Typography
        variant="caption"
        sx={{
          position: 'absolute',
          left: `calc(${factorPos}% + 16px)`,
          top: -6,
          fontWeight: 500,
          fontSize: 11,
          color: factorColor,
          transform: 'translateX(-50%)',
          zIndex: 5,
        }}
      >
        {factor}
      </Typography>
    </Box>
  );
};

export const DetailedNonLiveCFRangeGraphLegend = ({
  legendContainerProps,
}: any) => {
  const { config } = useTheme();
  const classes = useClasses();
  return (
    <Box className={classes.legendContainer} {...legendContainerProps}>
      <div className={classes.legendGrid}>
        <span
          className={classes.industryAverageLine}
          style={{ background: config.colors.grey6 }}
        />
        <span className={classes.legendText}>ICF</span>
      </div>
      <div className={classes.legendGrid}>
        <span
          className={classes.companyScoreDot}
          style={{ background: config.colors.grey2 }}
        />
        <span className={classes.legendText}>CCF</span>
      </div>
      <div className={classes.legendGrid}>
        <span
          className={classes.cfLegendBarRed}
          style={{ background: config.colors.grey3 }}
        />
        <span className={classes.legendText}>CCF {'<'} ICF</span>
      </div>
      <div className={classes.legendGrid}>
        <span
          className={classes.cfLegendBarGreen}
          style={{ background: config.colors.grey4 }}
        />
        <span className={classes.legendText}>CCF {'>'} ICF</span>
      </div>
    </Box>
  );
};

const useClasses = makeStyles(({ config }) => ({
  graphContainer: {
    position: 'relative',
    paddingTop: '5rem',
    margin: '0 auto',
  },
  innerLine: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '75%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '0.5rem',
    background: config.colors.grey2,
    padding: '5px 0',
  },
  average: {
    position: 'absolute',
    top: '100%',
    textAlign: 'center',
    transform: 'translateX(-50%)',
    '& > span': {
      display: 'block',
      borderBottom: `10px solid ${config.colors.cowbellBlue}`,
    },
    '& > div': {
      color: config.colors.cowbellBlue,
    },
  },
  nonLiveAverage: {
    '& > span': {
      borderBottom: `10px solid ${config.colors.grey2} !important`,
    },
    '& > div': {
      color: `${config.colors.grey2} !important`,
    },
  },
  factor: {
    position: 'absolute',
    textAlign: 'center',
    bottom: '100%',
    transform: 'translateX(-50%)',
    '& > span': {
      display: 'block',
      top: '0',
    },
  },
  graphTriangle: {
    borderLeft: '10px solid transparent',
    borderRight: '10px solid transparent',
  },
  legendContainer: {
    padding: '1rem',
    fontSize: '1rem',
    fontWeight: 500,
  },
  legendGrid: {
    display: 'grid',
    gridTemplateColumns: '32px 1fr',
    gap: '0.5rem',
    marginBottom: '0.25rem',
    minHeight: '24px',
    alignItems: 'center',
  },
  legendText: {
    textAlign: 'left',
    fontWeight: 400,
    lineHeight: '20px',
    display: 'flex',
    alignItems: 'center',
    height: '20px',
  },
  industryAverageDot: {
    background: config.colors.cfNeutral,
    borderRadius: '50%',
    height: '18px',
    width: '18px',
    display: 'inline-block',
    verticalAlign: 'middle',
    justifySelf: 'center',
  },
  cfLegendBarGreen: {
    height: '14px',
    width: '28px',
    borderRadius: '7px',
    background: config.colors.cfAbove,
    display: 'inline-block',
    verticalAlign: 'middle',
    justifySelf: 'center',
  },
  cfLegendBarRed: {
    height: '14px',
    width: '28px',
    borderRadius: '7px',
    background: config.colors.cfBelow,
    display: 'inline-block',
    verticalAlign: 'middle',
    justifySelf: 'center',
  },
  companyScoreLine: {
    height: '20px',
    width: '4px',
    background: 'black',
    display: 'inline-block',
    borderRadius: 2,
    verticalAlign: 'middle',
    justifySelf: 'center',
  },
  industryAverageLine: {
    height: '20px',
    width: '4px',
    background: 'black',
    display: 'inline-block',
    borderRadius: 2,
    verticalAlign: 'middle',
    justifySelf: 'center',
  },
  companyScoreDot: {
    background: config.colors.cfNeutral,
    borderRadius: '50%',
    height: '18px',
    width: '18px',
    display: 'inline-block',
    verticalAlign: 'middle',
    justifySelf: 'center',
  },
  legendRowSymbol: {
    width: '1.5rem',
    textAlign: 'center',
  },
  minMaxLegendRowSymbol: {
    background: config.colors.grey2,
    padding: '2px 0',
    borderRadius: '1rem',
  },
  legendTriangle: {
    display: 'inline-block',
    borderLeft: '5px solid transparent',
    borderRight: '5px solid transparent',
  },
  rangeValue: {
    position: 'absolute',
    top: '50%',
    color: config.colors.grey2,
  },
  minRangeValue: {
    left: `calc((100% - 75%) / 2 - 1.2rem)`,
    transform: 'translate(-50%, -50%)',
  },
  maxRangeValue: {
    right: `calc((100% - 75%) / 2 - 1.2rem)`,
    transform: 'translate(50%, -50%)',
  },
}));
